import React from 'react';
import { TextField, Button, Box, Typography, Divider, Avatar, Paper, FormControl, InputLabel, Select, MenuItem, CircularProgress, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';

const PostingContainer = styled(Paper)({
  padding: '56px 48px',
  width: '950px',
  minHeight: '600px',
  borderRadius: '20px',
  boxShadow: '0 6px 32px rgba(0,0,0,0.09)',
  backgroundColor: '#fff',
  border: '1px solid #dbdbdb'
});

interface CreatePostProps {
  postContent: string;
  setPostContent: (v: string) => void;
  imagePreviews: string[];
  imageFiles: File[];
  uploadUrl: string;
  handleImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveImage: (idx: number) => void;
  handlePostSubmit: (e: React.FormEvent) => void;
  handleCloseCreatePostModal: () => void;
  instagramPostLoading: boolean;
  selectedChannel: string;
  setSelectedChannel: (v: string) => void;
  imageUploadLoading: boolean;
}

const CreatePost: React.FC<CreatePostProps> = ({ postContent, setPostContent, imagePreviews, imageFiles, uploadUrl, handleImageUpload, handleRemoveImage, handlePostSubmit, handleCloseCreatePostModal, instagramPostLoading, selectedChannel, setSelectedChannel, imageUploadLoading }) => (

  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>

    <PostingContainer elevation={3} sx={{ position: 'relative' }}>
      <IconButton
        aria-label="close"
        onClick={handleCloseCreatePostModal}
        sx={{
          position: 'absolute',
          top: 18,
          right: 18,
          color: '#888',
          zIndex: 10,
          '&:hover': { color: '#6c63ff', background: '#f3f3f3' }
        }}
      >
        <CloseIcon fontSize="medium" />
      </IconButton>
      <Typography variant="h5" fontWeight={700} align="center" mb={3}>
        New social media post
      </Typography>

      <form onSubmit={handlePostSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 20 }}>


        <FormControl fullWidth>
          <InputLabel id="channel-select-label">Channel</InputLabel>
          <Select
            labelId="channel-select-label"
            id="channel-select"
            value={selectedChannel}
            label="Channel"
            onChange={(e) => setSelectedChannel(e.target.value as string)}
          >
            <MenuItem value="instagram">Instagram</MenuItem>
            <MenuItem value="facebook">Facebook</MenuItem>
            <MenuItem value="twitter">Twitter</MenuItem>
            <MenuItem value="linkedin">LinkedIn</MenuItem>
          </Select>
        </FormControl>

        <TextField
          placeholder="Share something with your followers..."
          multiline
          minRows={6}
          maxRows={16}
          variant="outlined"
          fullWidth
          value={postContent}
          onChange={e => setPostContent(e.target.value)}
          InputProps={{
            style: {
              fontSize: '16px',
              borderRadius: '10px',
              background: '#fcfcfc',
              boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
            },
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#e0e0e0',
              },
              '&:hover fieldset': {
                borderColor: '#a29bfe',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#6c63ff',
              },
            },
          }}
        />
        <Box mb={2} sx={{ position: 'relative', minHeight: '130px' }}>
  {/* File input disabled while loading */}
  <input
    accept="image/*,video/*"
    style={{ display: 'none' }}
    id="media-upload"
    type="file"
    multiple
    onChange={imageUploadLoading ? undefined : handleImageUpload}
    disabled={imageUploadLoading}
  />
  <label htmlFor="media-upload">
    <Box
      sx={{
        border: '2px dashed #b2bec3',
        borderRadius: '12px',
        background: '#f8fafd',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        cursor: imageUploadLoading ? 'not-allowed' : 'pointer',
        transition: 'border-color 0.2s',
        '&:hover': { borderColor: imageUploadLoading ? '#b2bec3' : '#6c63ff' },
        mb: 1.5,
        opacity: imageUploadLoading ? 0.5 : 1,
        pointerEvents: imageUploadLoading ? 'none' : 'auto',
      }}
      onDragOver={e => {
        if (imageUploadLoading) return;
        e.preventDefault();
        e.stopPropagation();
      }}
      onDrop={e => {
        if (imageUploadLoading) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
          // Create a synthetic event to reuse your handleImageUpload
          const dt = new DataTransfer();
          Array.from(e.dataTransfer.files).forEach(file => dt.items.add(file));
          const input = document.createElement('input');
          input.type = 'file';
          input.multiple = true;
          input.files = dt.files;
          handleImageUpload({ target: input } as any);
        }
      }}
    >
      <AttachFileIcon sx={{ fontSize: 32, color: '#6c63ff', mb: 1 }} />
      <Typography variant="body2" color="#6c63ff" fontWeight={600} mb={0.5}>
        Click or drag images/videos to upload
      </Typography>
      <Typography variant="caption" color="#b2bec3">
        JPG, PNG, GIF, MP4, MOV, AVI
      </Typography>
    </Box>
  </label>
  {/* Show previews only if not loading */}
  {!imageUploadLoading && imagePreviews.length > 0 && (
    <Box
      mt={2}
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
        gap: '16px',
        maxHeight: '340px',
        overflowY: 'auto',
        padding: '8px',
        borderRadius: '10px',
        backgroundColor: '#fafafa',
      }}
    >
      {imagePreviews.map((preview, index) => {
        const file = imageFiles && imageFiles[index];
        const isVideo = file && file.type && file.type.startsWith('video/');
        return (
          <Box
            key={index}
            sx={{
              position: 'relative',
              width: '100%',
              paddingBottom: '100%',
              borderRadius: '10px',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(80,80,80,0.07)',
            }}
          >
            {isVideo && uploadUrl ? (
              <video
                src={uploadUrl}
                controls
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '10px',
                }}
              />
            ) : (
              <img
                src={preview}
                alt={`Preview ${index + 1}`}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '10px',
                }}
              />
            )}
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                handleRemoveImage(index);
              }}
              sx={{
                position: 'absolute',
                top: '6px',
                right: '6px',
                minWidth: '22px',
                width: '22px',
                height: '22px',
                borderRadius: '50%',
                backgroundColor: 'rgba(0,0,0,0.6)',
                color: 'white',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2,
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.8)'
                }
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        );
      })}
    </Box>
  )}
  {/* Loading overlay */}
  {imageUploadLoading && (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(255,255,255,0.7)',
        zIndex: 10,
        borderRadius: '12px',
      }}
    >
      <CircularProgress color="primary" />
    </Box>
  )}
</Box>
        <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
          <Button
            variant="outlined"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              px: 3,
              color: '#6d6e7b',
              borderColor: '#d3d3d3',
              backgroundColor: '#f3f3f3',
              '&:hover': {
                backgroundColor: '#e0e0e0',
                borderColor: '#bdbdbd',
              },
            }}
            onClick={handleCloseCreatePostModal}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            sx={{ borderRadius: '8px', textTransform: 'none', px: 3, boxShadow: '0 2px 8px #6c63ff22', backgroundColor: '#6366f1', '&:hover': { backgroundColor: '#4f46e5' } }}

            disabled={instagramPostLoading}
          >
            {instagramPostLoading ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CircularProgress size={18} sx={{ color: 'white', mr: 1 }} />
                Sharing...
              </span>
            ) : (
              'Share'
            )}
          </Button>
        </Box>
      </form>
    </PostingContainer>
  </Box>
);

export default CreatePost;
